import { init, track as plausibleTrack } from '@plausible-analytics/tracker';

type Props = Record<string, string>;
type EventMap = Record<string, Props | undefined>;

export interface AnalyticsOptions {
  enabled: boolean;
  domain: string;
  endpoint?: string;
  ignorePaths?: string[];
  autoCapturePageviews?: boolean;
}

export interface Tracker<Events extends EventMap> {
  track<E extends keyof Events & string>(
    name: E,
    ...props: Events[E] extends undefined ? [] : [props: Events[E]]
  ): void;
  trackPageview(path?: string): void;
  setContext(context: Props | null): void;
}

let started = false;
let context: Props = {};

export function initAnalytics({
  enabled,
  domain,
  endpoint,
  ignorePaths = [],
  autoCapturePageviews = true,
}: AnalyticsOptions) {
  if (!enabled || started) return;

  try {
    init({
      domain,
      endpoint,
      autoCapturePageviews,
      logging: false,
      customProperties: () => context,
      transformRequest: (payload) =>
        ignorePaths.some((path) => window.location.pathname.startsWith(path)) ? null : payload,
    });
    started = true;
  } catch {
    started = false;
  }
}

function send(name: string, options: Parameters<typeof plausibleTrack>[1]) {
  if (!started) return;

  try {
    plausibleTrack(name, options);
  } catch {
    // Tracking must never break the app.
  }
}

export function createTracker<Events extends EventMap>(): Tracker<Events> {
  return {
    track(name, ...[props]) {
      send(name, props ? { props } : {});
    },
    trackPageview(path) {
      send('pageview', path ? { url: `${window.location.origin}${path}` } : {});
    },
    setContext(next) {
      context = next ?? {};
    },
  };
}
